import { css } from "@emotion/react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";

import MobileSubNavItem from "~/components/MobileSubNavItem";
import { SubItems } from "~/components/Subnav";

import BaseLink from "~/components/links/BaseLink";
import IconOnlyButton from "~/components/buttons/IconOnlyButton";

import { useTranslationContext } from "~/context/TranslationContext";

import transition from "~/utilities/transition";
import { navigateMobileMenuWithKeyboard } from "~/utilities/headerHelpers";
import { isBrowser, sanitizeForId } from "~/utilities/helpers";
import trackNavigationEvent from "~/utilities/trackNavigationEvent";
import { languageLabel } from "~/utilities/translations";

const sharedNavItemStyles = ({ theme }) => {
    return css`
        font-size: 2rem;
        font-weight: 700;
        display: block;
        width: 100%;
        padding: 10px 20px;
        color: ${theme.palette.purple};
    `;
};

const Wrapper = styled.div`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, 0);
    width: 100%;
    height: ${({ isOpen }) => (isOpen ? `100vh` : `0`)};
    overflow: hidden;
    overflow-y: scroll;
    background-color: ${({ theme }) =>
        theme.colors.backgroundColor.defaultLight};
    opacity: ${({ isOpen }) => (isOpen ? `1` : `0`)};
    visibility: ${({ isOpen }) => (isOpen ? `visible` : `hidden`)};
    z-index: 6;
`;

const TopSubNavWrapper = styled.ul`
    display: flex;
    justify-content: space-between;
    font-size: 2.2rem;
    padding: 10px 20px;
    background-color: ${({ theme }) => theme.palette.grayFive};
`;

const StyledIconButton = styled(IconOnlyButton)`
    color: ${({ theme }) => theme.palette.black};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.teal};
    }
`;

const H2 = styled.h2`
    display: ${({ isOpen }) => (isOpen ? "block" : "none")};
`;

const H3 = H2.withComponent("h3");

const MainNavLink = styled(BaseLink)`
    ${transition({}, "background-color", "text-decoration")};
    ${sharedNavItemStyles};

    &:hover,
    &:focus {
        text-decoration: underline;
        background-color: ${({ theme, small }) =>
            small ? "transparent" : theme.palette.grayFive};
    }
`;

const MainNav = styled.span`
    ${sharedNavItemStyles};
`;

const MobileSubNav = ({
    clearAllBodyScrollLocks = () => {},
    items = [],
    isOpen = false,
    linkLabel = "",
    parentLinkNativeName = "",
    linkUrl = "",
    newTabOverride = "default",
    openedSubnavs = {},
    dispatch = () => {},
    level = "secondary",
    index = -1,
    firstLinkLabel = "",
    firstLinkLabelIndex = -1,
    setFocusElementId = () => {},
}) => {
    const tertiaryLevel = level === "tertiary";
    const parentSubMenu = `${sanitizeForId(
        firstLinkLabel,
    )}-${firstLinkLabelIndex}`;
    const parentBtn = tertiaryLevel
        ? `${sanitizeForId(firstLinkLabel)}-${sanitizeForId(
              linkLabel,
          )}-${index}`
        : parentSubMenu;

    const mobileSubNavRef = useRef();
    const firstLinkRef = useRef();
    const finalLinkMobileRef = useRef();
    const finalMobileSubLinkRef = useRef();

    const { activeCode } = useTranslationContext();

    const handleBackClick = () => {
        dispatch({
            type: "CLOSE_MOBILE_SUB_NAV",
            level,
            parent: parentSubMenu,
        });

        const parentElement = document.getElementById(parentBtn);

        if (tertiaryLevel) {
            parentElement?.focus();
        } else {
            setFocusElementId(parentBtn);
        }
    };

    const handleMainNavClick = () => {
        trackNavigationEvent(
            false,
            tertiaryLevel ? 2 : 1,
            tertiaryLevel ? `${firstLinkLabel} > ${linkLabel}` : linkLabel,
            false,
        );
        clearAllBodyScrollLocks();
    };

    useEffect(() => {
        if (isBrowser && isOpen && firstLinkRef.current) {
            firstLinkRef.current.focus();
        }
    }, [isOpen]);

    const ConditionalMainNavHeader = tertiaryLevel ? H3 : H2;
    const ConditionalMainNav = linkUrl ? MainNavLink : MainNav;
    let mainNavProps = {};

    if (linkUrl) {
        mainNavProps = {
            to: linkUrl,
            newTabOverride,
            onClick: handleMainNavClick,
            tagRef: firstLinkRef,
        };
    }

    return (
        <Wrapper {...{ isOpen }}>
            <TopSubNavWrapper
                role="list"
                aria-labelledby="mobileNavLabel"
                ref={mobileSubNavRef}
            >
                <li>
                    <StyledIconButton
                        screenReaderText={
                            tertiaryLevel
                                ? `Back to ${firstLinkLabel} Menu`
                                : `Back to Main Navigation`
                        }
                        iconLibrary="fas"
                        icon="arrow-left"
                        onClick={handleBackClick}
                        onKeyDown={(keyboardEvent) =>
                            navigateMobileMenuWithKeyboard(
                                keyboardEvent,
                                mobileSubNavRef,
                            )
                        }
                    />
                </li>
                <li>
                    <StyledIconButton
                        screenReaderText="Close Navigation"
                        iconLibrary="fas"
                        icon="times"
                        onClick={() =>
                            dispatch({
                                type: "CLOSE_MOBILE_MENU",
                            })
                        }
                    />
                </li>
            </TopSubNavWrapper>

            <ConditionalMainNavHeader
                id={`SubNavHeading-${sanitizeForId(linkLabel)}`}
                {...{ isOpen }}
            >
                <ConditionalMainNav {...mainNavProps}>
                    {linkLabel}
                </ConditionalMainNav>
            </ConditionalMainNavHeader>

            <SubItems
                aria-labelledby={`SubNavHeading-${sanitizeForId(linkLabel)}`}
            >
                {items.map(
                    (
                        {
                            linkLabel: subLinkLabel,
                            linkUrl,
                            linkNativeName,
                            linkTranslatedName,
                            newTabOverride,
                            subItems,
                            languageCode,
                        },
                        currentIndex,
                    ) => {
                        const props = {
                            subItems,
                            languageCode,
                            parentLinkLabel: parentLinkNativeName ?? linkLabel,
                            linkLabel: languageLabel(
                                subLinkLabel,
                                linkNativeName,
                                linkTranslatedName,
                                languageCode,
                                activeCode,
                            ),
                            linkNativeName,
                            linkUrl,
                            newTabOverride,
                            key: linkNativeName ?? subLinkLabel,
                            clearAllBodyScrollLocks,
                            level,
                            dispatch,
                            openedSubnavs,
                            firstLinkLabel,
                            firstLinkLabelIndex,
                            index: currentIndex,
                            parentIndex: index,
                        };

                        if (currentIndex === items.length - 1) {
                            props.onKeyDown = (keyboardEvent) =>
                                navigateMobileMenuWithKeyboard(
                                    keyboardEvent,
                                    mobileSubNavRef,
                                );
                            props.tagRef = finalLinkMobileRef;
                            props.subTagRef = finalMobileSubLinkRef;
                        }
                        if (
                            currentIndex === 0 &&
                            ConditionalMainNav === MainNav
                        ) {
                            props.tagRef = firstLinkRef;
                        }
                        return <MobileSubNavItem {...props} />;
                    },
                )}
            </SubItems>
        </Wrapper>
    );
};

MobileSubNav.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})),
    isOpen: PropTypes.bool,
    clearAllBodyScrollLocks: PropTypes.func,
    linkLabel: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    linkUrl: PropTypes.string,
    newTabOverride: PropTypes.string,
    openedSubnavs: PropTypes.shape({}),
    dispatch: PropTypes.func,
    setFocusElementId: PropTypes.func,
    level: PropTypes.string,
    firstLinkLabel: PropTypes.string,
    firstLinkLabelIndex: PropTypes.number,
    parentLinkNativeName: PropTypes.string,
    index: PropTypes.number,
    dangerouslySetLabels: PropTypes.bool,
};

export default MobileSubNav;
